import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { getClients, searchClients, clearSearchResults } from '../redux/slices/clientSlice';
import { Search, Plus, User, Building, Mail, Phone, Check } from 'lucide-react';
import toast from 'react-hot-toast';

const ClientSelector = ({ onClientSelect, currentClient, onClose }) => {
  const dispatch = useDispatch();
  const { clients, searchResults, isLoading } = useSelector((state) => state.client);
  const { user } = useSelector((state) => state.auth);
  
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [selectedClientId, setSelectedClientId] = useState(null);

  // Check if user has client management feature
  const hasClientManagement = user?.subscription?.plan_id?.features?.clientManagement;

  useEffect(() => {
    if (hasClientManagement) {
      dispatch(getClients());
    }
  }, [dispatch, hasClientManagement]);

  const handleSearch = async (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      await dispatch(searchClients(searchQuery));
      setShowSearchResults(true);
    }
  };

  const handleClientClick = (client) => {
    setSelectedClientId(client._id);
    
    // Format client data for invoice
    const clientData = {
      name: client.name,
      email: client.email,
      company: client.company || '',
      address: client.address ? 
        `${client.address.street || ''}\n${client.address.city || ''}, ${client.address.state || ''} ${client.address.zipCode || ''}\n${client.address.country || ''}`.trim() : '',
      phone: client.phone || '',
    };
    
    onClientSelect(clientData);
    toast.success(`${client.name} selected`);
  };

  const clearSearch = () => {
    setSearchQuery('');
    setShowSearchResults(false);
    dispatch(clearSearchResults());
  };

  if (!hasClientManagement) {
    return (
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-center">
          <Building className="w-5 h-5 text-blue-600 mr-2" />
          <div>
            <p className="text-sm font-medium text-blue-900">Client Management Available</p>
            <p className="text-xs text-blue-700">Upgrade to Starter or Pro plan to save and select clients</p>
          </div>
        </div>
      </div>
    );
  }

  const displayClients = showSearchResults ? searchResults : clients;

  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-lg max-w-md">
      {/* Header */}
      <div className="p-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900">Select Client</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            ×
          </button>
        </div>
      </div>

      {/* Search */}
      <div className="p-4 border-b border-gray-200">
        <form onSubmit={handleSearch} className="space-y-2">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search clients..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="input pl-10 text-sm"
            />
          </div>
          <div className="flex space-x-2">
            <button type="submit" className="btn btn-sm btn-secondary">
              Search
            </button>
            {showSearchResults && (
              <button 
                type="button" 
                onClick={clearSearch}
                className="btn btn-sm btn-outline"
              >
                Clear
              </button>
            )}
          </div>
        </form>
      </div>

      {/* Clients List */}
      <div className="max-h-96 overflow-y-auto">
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-primary-600"></div>
          </div>
        ) : displayClients.length === 0 ? (
          <div className="p-4 text-center">
            <User className="w-12 h-12 text-gray-300 mx-auto mb-2" />
            <p className="text-sm text-gray-600">
              {showSearchResults ? 'No clients found' : 'No clients yet'}
            </p>
            {!showSearchResults && (
              <p className="text-xs text-gray-500 mt-1">
                Add clients in the Clients section
              </p>
            )}
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {displayClients.map((client) => (
              <div
                key={client._id}
                onClick={() => handleClientClick(client)}
                className={`p-4 cursor-pointer hover:bg-gray-50 transition-colors ${
                  selectedClientId === client._id ? 'bg-primary-50' : ''
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2">
                      <h4 className="font-medium text-gray-900">{client.name}</h4>
                      {selectedClientId === client._id && (
                        <Check className="w-4 h-4 text-primary-600" />
                      )}
                    </div>
                    
                    {client.company && (
                      <div className="flex items-center space-x-1 mt-1">
                        <Building className="w-3 h-3 text-gray-400" />
                        <span className="text-sm text-gray-600">{client.company}</span>
                      </div>
                    )}
                    
                    <div className="flex items-center space-x-1 mt-1">
                      <Mail className="w-3 h-3 text-gray-400" />
                      <span className="text-sm text-gray-600">{client.email}</span>
                    </div>
                    
                    {client.phone && (
                      <div className="flex items-center space-x-1 mt-1">
                        <Phone className="w-3 h-3 text-gray-400" />
                        <span className="text-sm text-gray-600">{client.phone}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="p-4 border-t border-gray-200 bg-gray-50">
        <Link
          to="/clients/new"
          onClick={onClose}
          className="w-full btn btn-sm btn-outline flex items-center justify-center"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add New Client
        </Link>
      </div>
    </div>
  );
};

export default ClientSelector;
